import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Alert, Button, Divider, Grid, Link, Paper, Skeleton, Typography } from "@mui/material";
import { DateTime } from "luxon";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { fetchLatestPermanentBid } from "../../api/bid";
import { selectUserSession } from "../../slices/userSession/userSessionSlice";
import { isPermBidEligible } from "../../util/bid/bidCommon";
import { getShiftDate } from "../../util/dateTimeFormatter";
import { getUnionContractGivenJobCode } from "../../util/workgroupMapper";

const DashboardEmployeePermanentBid = () => {
  const userSession = useSelector(selectUserSession);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState("");
  const [permanentBid, setPermanentBid] = useState(null);

  useEffect(() => {
    if (isPermBidEligible(getUnionContractGivenJobCode(userSession.jobCode))) {
      fetchPermanentBidData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPermanentBidData = async () => {
    try {
      const response = await fetchLatestPermanentBid(userSession.eid);
      if (response.status === 200) {
        const data = await response.json();
        setPermanentBid(data.latestPermanentBidEvent);
      }
    } catch (error) {
      console.error("Error fetching permanent bid: ", error);
      setFetchError("Unexpected Error");
    } finally {
      setIsLoading(false);
    }
  };

  const renderModifiedByName = () => {
    return `${permanentBid.modifiedByLastName}, ${permanentBid.modifiedByFirstName}`;
  };

  const renderCreatedAt = () => {
    const createdAt = DateTime.fromMillis(parseInt(permanentBid.createdAt));
    return `${getShiftDate(createdAt)}, ${createdAt.toFormat("HH:mm ZZZZ")}`;
  };

  if (!isPermBidEligible(getUnionContractGivenJobCode(userSession.jobCode))) return <></>;

  return (
    <Paper
      variant="outlined"
      sx={{
        backgroundColor: "secondary.swaGray1",
        borderRadius: "5px",
        display: "flex",
        flexDirection: "column",
        paddingLeft: "1.5rem",
        paddingRight: "1.5rem",
        paddingTop: "1rem",
        paddingBottom: "1rem",
      }}
    >
      <Grid item xs={12}>
        <Typography variant="h6" component="div" style={{ textAlign: "left", fontWeight: "bold" }}>
          My Permanent Bid
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Divider />
      </Grid>
      {isLoading ? (
        <Skeleton height={100} />
      ) : fetchError !== "" ? (
        <Alert
          variant="filled"
          severity="error"
          sx={{ display: "flex", width: "200px", alignSelf: "center", margin: "1rem" }}
        >
          {fetchError}
        </Alert>
      ) : (
        <Paper elevation={1} sx={{ marginBottom: "10px", padding: "10px", verticalAlign: "middle", marginTop: "20px" }}>
          <Grid container sx={{ textAlign: "left" }} alignItems="center">
            <Grid item xs={12} sx={{ marginBottom: "10px" }}>
              {permanentBid ? (
                <Typography variant="body2" fontWeight="bold" sx={{ display: "flex", alignItems: "center" }}>
                  <CheckCircleIcon color="success" sx={{ fontSize: "40px", mr: 1 }} />
                  Last updated by {renderModifiedByName()} on {renderCreatedAt()}
                </Typography>
              ) : (
                <Typography variant="body2" fontWeight="bold" sx={{ display: "flex", alignItems: "center" }}>
                  <CancelIcon color="error" sx={{ fontSize: "40px", mr: 1 }} />
                  You do not have a Permanent Bid on file, enter one now!
                </Typography>
              )}
            </Grid>
            <Grid item xs={12}>
              <Link href="#/myPermanentBid">
                <Button color="primary" variant="contained" sx={{ color: "secondary.swaWhite", fontWeight: "bold" }}>
                  UPDATE
                </Button>
              </Link>
            </Grid>
          </Grid>
        </Paper>
      )}
    </Paper>
  );
};

export default DashboardEmployeePermanentBid;
